import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as configApi from '../api/config';

export function AdminIncidentCategories() {
  const { selectedOrg } = useOrg();
  const { canAdmin } = usePermissions();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    severity: 'medium',
    description: ''
  });

  const loadCategories = useCallback(async () => {
    if (!selectedOrg) return;
    setLoading(true);
    setError(null);
    try {
      const cats = await configApi.getIncidentCategories(selectedOrg.id);
      setCategories(cats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleOpenCreate = () => {
    setEditingCategory(null);
    setFormData({ name: '', code: '', severity: 'medium', description: '' });
    setShowModal(true);
  };

  const handleOpenEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      code: category.code,
      severity: category.severity,
      description: category.description || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;

    try {
      if (editingCategory) {
        await configApi.updateIncidentCategory(selectedOrg.id, editingCategory.id, formData);
      } else {
        await configApi.createIncidentCategory(selectedOrg.id, formData);
      }
      setShowModal(false);
      loadCategories();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (category) => {
    if (!selectedOrg) return;
    try {
      await configApi.updateIncidentCategory(selectedOrg.id, category.id, {
        isActive: !category.isActive
      });
      loadCategories();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!canAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to manage incident categories.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Incident Categories"
        subtitle="Configure incident categorization taxonomy"
      />

      <div style={styles.actions}>
        <button
          onClick={handleOpenCreate}
          style={styles.createButton}
          data-testid="create-category-button"
        >
          Add Category
        </button>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading categories...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="categories-table">
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Code</th>
                <th style={styles.th}>Severity</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(category => (
                <tr key={category.id} style={styles.tr} data-testid={`category-${category.id}`}>
                  <td style={styles.td}>{category.name}</td>
                  <td style={styles.td}>
                    <code style={styles.code}>{category.code}</code>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.severityBadge,
                      background: getSeverityColor(category.severity)
                    }}>
                      {category.severity}
                    </span>
                  </td>
                  <td style={styles.td}>{category.description}</td>
                  <td style={styles.td}>v{category.version}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      background: category.isActive ? '#c6f6d5' : '#fed7d7',
                      color: category.isActive ? '#276749' : '#c53030'
                    }}>
                      {category.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleOpenEdit(category)}
                      style={styles.editButton}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(category)}
                      style={styles.toggleButton}
                    >
                      {category.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && (
                <tr>
                  <td colSpan="7" style={styles.emptyState}>
                    No categories configured
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="category-modal">
            <h3 style={styles.modalTitle}>
              {editingCategory ? 'Edit Category' : 'Create Category'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={styles.input}
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  style={styles.input}
                  required
                  disabled={!!editingCategory}
                  data-testid="category-code-input"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Severity</label>
                <select
                  value={formData.severity}
                  onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                  style={styles.select}
                  data-testid="category-severity-select"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={styles.textarea}
                  data-testid="category-description-input"
                />
              </div>
              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.submitButton}
                  data-testid="save-category-button"
                >
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function getSeverityColor(severity) {
  const colors = {
    high: '#fc8181',
    medium: '#f6e05e',
    low: '#68d391'
  };
  return colors[severity] || '#e2e8f0';
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  actions: {
    marginBottom: '1rem'
  },
  createButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem'
  },
  code: {
    background: '#edf2f7',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem'
  },
  severityBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  statusBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  editButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #3182ce',
    background: 'white',
    color: '#3182ce',
    fontSize: '0.75rem',
    cursor: 'pointer',
    marginRight: '0.5rem'
  },
  toggleButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #718096',
    background: 'white',
    color: '#718096',
    fontSize: '0.75rem',
    cursor: 'pointer'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '400px',
    maxWidth: '500px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  modalTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  formGroup: {
    marginBottom: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#4a5568'
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  select: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  textarea: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    minHeight: '80px',
    resize: 'vertical'
  },
  modalActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem'
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  }
};
